import { useCallback, useEffect, useState } from "react";
import { Loader2, Trash2 } from "lucide-react";

import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Switch } from "./ui/switch";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "./ui/alert-dialog";
import { fetchPhotoRetention, runPhotoRetention, savePhotoRetention } from "../lib/api";
import { toast } from "sonner";
import { salvageShort } from "../lib/company";

const mb = (b) => ((b || 0) / 1e6).toFixed(0);

/**
 * Photo retention. The window is measured from when the job SETTLED — the
 * vehicle left the yard or the matter closed — not from the tow, so a car
 * still sitting in storage keeps its photos however old the job is.
 */
export const PhotoRetentionRow = () => {
  const salvage = salvageShort();
  const [report, setReport] = useState(null);
  const [days, setDays] = useState(60);
  const [busy, setBusy] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async (d) => {
    setBusy(true);
    try {
      const r = await fetchPhotoRetention(d);
      setReport(r);
      setDays(r.retention_days);
    } catch (e) {
      // A swallowed failure here is dangerous: the previous report stays on
      // screen and reads as if it describes the window you just typed.
      setReport(null);
      toast.error(e.response?.data?.detail || "Could not read the photo report");
    } finally {
      setBusy(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const toggle = async (enabled) => {
    try {
      await savePhotoRetention(enabled, days);
      setReport((r) => ({ ...r, enabled }));
      toast.success(enabled ? "Automatic cleanup is on" : "Automatic cleanup is off");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save");
    }
  };

  const purge = async () => {
    setDeleting(true);
    try {
      const res = await runPhotoRetention();
      toast.success(
        `Deleted photos on ${res.jobs_processed} job(s) — ${mb(res.bytes_freed)}MB freed`,
      );
      load(days);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Cleanup failed");
    } finally {
      setDeleting(false);
    }
  };

  const held = Object.entries(report?.held_back || {});

  return (
    <div
      className="mt-3 rounded-md border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/40 px-4 py-3"
      data-testid="photo-retention-row"
    >
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="text-sm font-semibold text-slate-800 dark:text-slate-100">
            Photo Retention
          </div>
          <div className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
            Deletes stored images once a job has been settled for the window
            below — measured from the day the vehicle left the yard or the
            matter closed, not the day of the tow. Impounds still held are
            never touched, nor is {salvage} sign-over paperwork. The job
            record and each photo&apos;s details (who took it, when, captions)
            are kept forever.
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <Switch
            checked={!!report?.enabled}
            onCheckedChange={toggle}
            data-testid="photo-retention-enabled"
          />
          <span className="text-xs text-slate-500 dark:text-slate-400">Automatic</span>
        </div>
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-2">
        <Input
          type="number"
          value={days}
          min={7}
          max={3650}
          onChange={(e) => setDays(Number(e.target.value))}
          className="h-8 w-20"
          data-testid="photo-retention-days"
        />
        <span className="text-xs text-slate-500 dark:text-slate-400">days</span>
        <Button
          variant="outline"
          size="sm"
          onClick={() => load(days)}
          disabled={busy}
          data-testid="photo-retention-scan-btn"
        >
          {busy ? <Loader2 className="mr-1 h-3 w-3 animate-spin" /> : null}
          Dry run
        </Button>
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              disabled={deleting || !report?.jobs_pending}
              data-testid="photo-retention-run-btn"
            >
              {deleting ? <Loader2 className="mr-1 h-3 w-3 animate-spin" /> : <Trash2 className="mr-1 h-3 w-3" />}
              Delete them now
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent data-testid="photo-retention-confirm">
            <AlertDialogHeader>
              <AlertDialogTitle>
                Delete {report?.photos_pending} photo
                {report?.photos_pending === 1 ? "" : "s"}?
              </AlertDialogTitle>
              <AlertDialogDescription>
                This frees about {mb(report?.bytes_pending)}MB across{" "}
                {report?.jobs_pending} settled job
                {report?.jobs_pending === 1 ? "" : "s"}. The images cannot be
                recovered. Everything listed as held back stays where it is.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="photo-retention-cancel">
                Cancel
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={purge}
                className="bg-rose-600 text-white hover:bg-rose-700"
                data-testid="photo-retention-confirm-btn"
              >
                Yes, delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>

      {report ? (
        <div
          className="mt-2 font-mono text-xs text-slate-600 dark:text-slate-300"
          data-testid="photo-retention-status"
        >
          {report.jobs_pending === 0 ? (
            <span className="text-emerald-700 dark:text-emerald-400">
              ✓ Nothing past {report.retention_days} days —{" "}
              {report.jobs_with_photos} job(s) still holding photos, all of them
              kept on purpose.
            </span>
          ) : (
            <span className="text-amber-700 dark:text-amber-400">
              {report.jobs_pending} job(s) · {report.photos_pending} photo(s) ·{" "}
              {mb(report.bytes_pending)}MB settled more than{" "}
              {report.retention_days} days ago
            </span>
          )}
          {held.length ? (
            <ul className="mt-1.5 space-y-0.5 text-slate-500 dark:text-slate-400">
              {held.map(([reason, v]) => (
                <li key={reason}>
                  kept · {v.photos} photo(s), {mb(v.bytes)}MB — {reason}
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};

export default PhotoRetentionRow;
